import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons/faAngleDown';
import { faAngleUp } from '@fortawesome/free-solid-svg-icons/faAngleUp';

import Card from 'components/Account/Common/Card/Card';
import CustomLink from 'components/Common/CustomLink/CustomLink';
import { ScrollToTop } from 'components/Common/ScrollToTop/ScrollToTop';
import { formatPrice } from 'services/formatter';
import { getRoutesUri } from 'services/generic';
import { DataDetailsEvtOrders, DataDetailsOrders } from 'types/DetailsMyOrders';

import styles from './MyOrdersList.module.scss';

function MyOrdersList({
  evtordersdata,
  ordersdata,
}: {
  evtordersdata: DataDetailsEvtOrders;
  ordersdata: DataDetailsOrders;
}): JSX.Element {
  const [maxOrders, setMaxOrders] = useState(5);
  const [scrollTop, setScrollTop] = useState(false);
  const { t } = useTranslation(['ordersPage']);
  const totalOrders = ordersdata.length + evtordersdata.length;

  return (
    <>
      {scrollTop && <ScrollToTop />}
      <Card classes={{ container: styles.content }}>
        <div className={styles.titleArea}>
          <label className={styles.desktopTitle}>
            {t('LABEL_ORDER_LAST_ORDERS')}
          </label>
          <label className={styles.mobileTitle}>
            {t('LABEL_MYACCOUNT_ORDER')}
          </label>
        </div>
        <table>
          <thead>
            <tr>
              <th className={styles.leftAlignTable}>{t('LABEL_ORDER_DATE')}</th>
              <th>{t('LABEL_NUMBER_ORDER')}</th>
              <th>{t('LABEL_DELIVERY_SHIPPING_METHOD')}</th>
              <th className={styles.total}>{t('LABEL_ORDER_TOTAL')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {evtordersdata
              .slice(0, maxOrders)
              .map(({ DATE_EVT, CODE_EVT, MTT_TOTAL, MODE_LIVRAISON }) => (
                <Item
                  key={CODE_EVT}
                  id={CODE_EVT}
                  date={DATE_EVT}
                  total={+MTT_TOTAL}
                  shippingMethod={MODE_LIVRAISON}
                  isEvt
                />
              ))}

            {ordersdata
              .slice(0, maxOrders - evtordersdata.length)
              .map(({ DATE_CDE, NUM_CDE, MTT_TOTAL, MODE_LIVRAISON }) => (
                <Item
                  key={NUM_CDE}
                  id={NUM_CDE}
                  date={DATE_CDE}
                  total={+MTT_TOTAL}
                  shippingMethod={MODE_LIVRAISON}
                />
              ))}
          </tbody>
        </table>
        {totalOrders <= maxOrders && maxOrders <= 5 ? (
          ''
        ) : maxOrders < totalOrders ? (
          <div
            className={styles.seeButton}
            onClick={() => setMaxOrders(totalOrders)}
          >
            <div className={styles.seeButtonContent}>
              <span onClick={() => setScrollTop(false)}>
                {t('LABEL_ORDER_SEE_MORE')}
                <FontAwesomeIcon icon={faAngleDown} />
              </span>
            </div>
          </div>
        ) : (
          <div className={styles.seeButton} onClick={() => setMaxOrders(5)}>
            <div className={styles.seeButtonContent}>
              <span onClick={() => setScrollTop(true)}>
                {t('LABEL_ORDER_SEE_LESS')}
                <FontAwesomeIcon icon={faAngleUp} />
              </span>
            </div>
          </div>
        )}
      </Card>
    </>
  );
}

export default MyOrdersList;

function Item({
  date,
  id,
  total,
  shippingMethod,
  isEvt = false,
}: {
  date: string;
  id: string;
  total: number;
  shippingMethod: string;
  isEvt?: boolean;
}): JSX.Element {
  const { t } = useTranslation(['ordersPage']);

  return (
    <tr>
      <td className={styles.leftAlignTable}>
        <strong>
          {t('TABLE_HEAD_TITLE_FORMAT', {
            title: t('LABEL_ORDER_DATE'),
          })}
        </strong>
        <label>{t('LABEL_DATE_SHORT', { date: date })}</label>
      </td>
      <td>
        <strong>
          {t('TABLE_HEAD_TITLE_FORMAT', {
            title: t('LABEL_NUMBER_ORDER'),
          })}
        </strong>
        <label>{id}</label>
      </td>
      <td>
        <strong>
          {t('TABLE_HEAD_TITLE_FORMAT', {
            title: t('LABEL_DELIVERY_STATUS'),
          })}
        </strong>
        <label>{shippingMethod}</label>
      </td>
      <td>
        <strong>
          {t('TABLE_HEAD_TITLE_FORMAT', {
            title: t('LABEL_ORDER_TOTAL'),
          })}
        </strong>
        <label>{formatPrice(total)}</label>
      </td>
      <td className={styles.seeDetailAction}>
        <CustomLink
          to={getRoutesUri<string>(
            isEvt
              ? 'customerAccountOrderEvtDetails'
              : 'customerAccountOrderDetails',
            {
              id: id,
            }
          )}
        >
          <span>{t('LABEL_ORDER_SEE_DETAIL')}</span>
        </CustomLink>
      </td>
    </tr>
  );
}
